import { Menu as BaseMenu } from "@base-ui/react/menu";
import { ChevronRight } from "lucide-react";
import { useEffect, useId, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { twMerge } from "tailwind-merge";
import {
    FLOATING_LIST_PANEL_CLASS,
    FLOATING_PANEL_CLASS,
    FLOATING_POSITIONER_CLASS,
    FLOATING_ROW_CLASS,
    FLOATING_SCROLLING_LIST_CLASS,
    FloatingRowContent,
    FloatingRowCount,
} from "../FloatingPanel";
import { useMenuLevels } from "./menuLevels";

type LevelRowProps = {
    label: ReactNode;
    children: ReactNode;
    icon?: ReactNode;
    disabled?: boolean;
    /** Marks the row as the one in use, such as a custom range that is applied, with the chosen-row background. */
    current?: boolean;
    /** Shown before the chevron, such as how many values are picked inside. */
    count?: number;
};

const Chevron = () => <ChevronRight className="h-4 w-4 text-stone-400" />;

/** The count, when there is one, then the chevron. */
const RowTrailing = (props: { count?: number }) => (
    <>
        {props.count !== undefined && <FloatingRowCount count={props.count} />}
        <Chevron />
    </>
);

/** Row that opens its children as a level */
const LevelRow = (props: LevelRowProps & { kind: "submenu" | "panel" }) => {
    const levels = useMenuLevels(props.kind === "panel" ? "MenuPanel" : "Submenu");
    const id = useId();
    // Finds this level among the open ones. It is showing when it is the last.
    const depth = levels.openLevels.findIndex(level => level.id === id);
    const isShowing = depth !== -1 && depth === levels.openLevels.length - 1;
    const openingRowRef = useRef<HTMLDivElement>(null);

    // Keyboard back: returns focus to the row that opened this level.
    useEffect(() => {
        const change = levels.lastLevelChange;
        if (change === null || change.levelId !== id || change.direction !== "left" || !change.byKeyboard) {
            return;
        }
        openingRowRef.current?.focus();
        // Falls back to the menu when the row is hidden, such as a MenuPanel opened from a closed fly-out.
        if (document.activeElement !== openingRowRef.current) {
            levels.focusMenu();
        }
    }, [levels.lastLevelChange]);

    // Opens this level. detail is 0 for clicks from Enter or Space.
    const openThisLevel = (event: MouseEvent) => {
        const level = { id, label: props.label };
        levels.openLevel(level, { byKeyboard: event.detail === 0 });
    };

    return (
        <>
            {/* The row that opens this level. */}
            <BaseMenu.Item
                ref={openingRowRef}
                className={twMerge(FLOATING_ROW_CLASS, props.current === true && "bg-stone-100")}
                aria-current={props.current === true ? "true" : undefined}
                disabled={props.disabled}
                closeOnClick={false}
                onClick={openThisLevel}
            >
                <FloatingRowContent icon={props.icon} trailing={<RowTrailing count={props.count} />}>
                    {props.label}
                </FloatingRowContent>
            </BaseMenu.Item>
            {/* This level's content, rendered into the menu while it is open. */}
            {depth !== -1 &&
                levels.levelContainer !== null &&
                createPortal(
                    // Outer levels stay mounted but hidden so nested levels keep rendering.
                    <div className={isShowing ? "contents" : "hidden"}>
                        {props.children}
                    </div>,
                    levels.levelContainer,
                )}
        </>
    );
};

/** Row that opens more menu items: beside the menu on wider screens, as a level on phones. Can hold anything a Menu can. */
export const Submenu = (props: LevelRowProps & { /** Sizing for the submenu when it opens beside the menu. */ className?: string }) => {
    const levels = useMenuLevels("Submenu");
    const [open, setOpen] = useState(false);

    // On phones, opens as a level instead of beside the menu.
    if (levels.submenusAsLevels) {
        return <LevelRow {...props} kind="submenu" />;
    }

    // On wider screens, opens beside the menu.
    return (
        // Closed while a level is open, since its trigger row is hidden.
        <BaseMenu.SubmenuRoot open={open && levels.openLevels.length === 0} onOpenChange={setOpen} disabled={props.disabled}>
            <BaseMenu.SubmenuTrigger
                className={twMerge(FLOATING_ROW_CLASS, "data-popup-open:bg-stone-100", props.current === true && "bg-stone-100")}
                aria-current={props.current === true ? "true" : undefined}
            >
                <FloatingRowContent icon={props.icon} trailing={<RowTrailing count={props.count} />}>
                    {props.label}
                </FloatingRowContent>
            </BaseMenu.SubmenuTrigger>
            {/* Kept mounted while a level is open, in case it is a MenuPanel rendered from this submenu. */}
            <BaseMenu.Portal keepMounted={levels.openLevels.length > 0}>
                <BaseMenu.Positioner className={FLOATING_POSITIONER_CLASS} sideOffset={8}>
                    <BaseMenu.Popup className={twMerge(FLOATING_PANEL_CLASS, FLOATING_LIST_PANEL_CLASS, props.className)}>
                        <div className={FLOATING_SCROLLING_LIST_CLASS}>{props.children}</div>
                    </BaseMenu.Popup>
                </BaseMenu.Positioner>
            </BaseMenu.Portal>
        </BaseMenu.SubmenuRoot>
    );
};

/**
 * Stops keys typed in a field inside a menu, or in a container of fields, from triggering the menu's typeahead,
 * arrow-key navigation, or closing on Tab. Escape still closes the menu. MenuPanel applies it to its content;
 * pass it to onKeyDown for a field placed directly among menu items.
 *
 * <Input value={search} onChange={...} onKeyDown={stopKeyPropagation} />
 */
export const stopKeyPropagation = (event: KeyboardEvent) => {
    if (event.key !== "Escape") {
        event.stopPropagation();
    }
};

/**
 * Row that opens any content, such as a small form, as a level on every screen size.
 * The content sets its own width and can call useMenuPanel() to go back or close.
 *
 * <MenuPanel label="Custom range" icon={<Calendar />}>
 *     <CustomRangeForm />
 * </MenuPanel>
 */
export const MenuPanel = (props: LevelRowProps) => (
    <LevelRow label={props.label} icon={props.icon} disabled={props.disabled} current={props.current} count={props.count} kind="panel">
        <div onKeyDown={stopKeyPropagation}>{props.children}</div>
    </LevelRow>
);
